import { motion } from "framer-motion";
import { Upload, ScanSearch, ClipboardCheck, HeartPulse, ArrowRight } from "lucide-react";
import { Reveal, SectionHeading, EASE } from "./shared";

const STEPS = [
  {
    num: "01",
    icon: Upload,
    title: "Patient Uploads Progress",
    copy: "Patients share photos, updates and concerns from the mobile app in seconds.",
    color: "#2563EB",
  },
  {
    num: "02",
    icon: ScanSearch,
    title: "AI Reviews & Organizes",
    copy: "Alignexa structures everything into a clean, clinical-ready summary.",
    color: "#6366F1",
  },
  {
    num: "03",
    icon: ClipboardCheck,
    title: "Doctor Reviews Summary",
    copy: "Orthodontists approve AI-drafted notes and priorities in moments, not hours.",
    color: "#0EA5E9",
  },
  {
    num: "04",
    icon: HeartPulse,
    title: "Better Patient Care",
    copy: "More time chairside. Faster decisions. Happier patients and teams.",
    color: "#22C55E",
  },
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" data-testid="how-section" className="relative py-24 lg:py-32 bg-[#F8FAFC] overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[40rem] h-[20rem] rounded-full bg-[#6366F1]/8 blur-[100px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6">
        <SectionHeading
          testId="how-heading"
          eyebrow="How it works"
          title="From upload to better care in four steps"
          sub="A quiet, intelligent pipeline between your patients and your chair — no new habits required."
        />

        <div className="relative mt-20">
          {/* Connecting line */}
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.6, ease: EASE, delay: 0.3 }}
            className="hidden lg:block absolute top-10 left-[12%] right-[12%] h-px bg-gradient-to-r from-[#2563EB]/30 via-[#6366F1]/40 to-[#22C55E]/30 origin-left"
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
            {STEPS.map((step, i) => (
              <Reveal key={step.num} delay={0.15 + i * 0.15}>
                <div className="group relative text-center" data-testid={`how-step-${step.num}`}>
                  <div className="relative inline-flex">
                    <motion.div
                      animate={{ y: [0, -8, 0] }}
                      transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.6 }}
                      className="w-20 h-20 rounded-3xl bg-white border border-slate-200/80 shadow-[0_16px_40px_rgba(15,23,42,0.08)] flex items-center justify-center transition-[box-shadow,border-color] duration-500 group-hover:shadow-[0_24px_50px_rgba(37,99,235,0.2)] group-hover:border-[#2563EB]/30"
                    >
                      <step.icon className="w-8 h-8" style={{ color: step.color }} strokeWidth={1.6} />
                    </motion.div>
                    <span className="absolute -top-2.5 -right-2.5 font-display text-[11px] font-semibold text-white rounded-full px-2 py-0.5 shadow-md" style={{ backgroundColor: step.color }}>
                      {step.num}
                    </span>
                  </div>
                  <h3 className="font-display font-medium text-xl tracking-tight text-[#0F172A] mt-6">
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed mt-2.5 max-w-[16rem] mx-auto">
                    {step.copy}
                  </p>
                  {i < STEPS.length - 1 && (
                    <ArrowRight className="hidden lg:block absolute top-8 -right-5 w-5 h-5 text-[#6366F1]/50" strokeWidth={1.8} />
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
